import { useState, useEffect } from "react";
import { ArrowLeft, Phone, Search, Merge } from "lucide-react";
import { api } from "../api";
import { useToast } from "../components/Toast.jsx";
import YClientsRecordPickerModal from "../components/YClientsRecordPickerModal.jsx";
import PatientMergePanel from "../components/PatientMergePanel.jsx";
import { useDebouncedValue } from "../lib/useDebouncedValue.js";
import PatientAlias from "../components/PatientAlias.jsx";

// «Разобрать неразобранное» — сверка пациентов ScanApp (без yclients_client_id) с клиентами
// YClients: сначала матч самого пациента, потом (если есть) — привязка уже сохранённых сканов
// этого пациента к конкретным записям. Central-only (см. requireCentralAdmin на бэкенде).
export default function ReconcilePage() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null); // выбранный на сверку пациент
  const { showToast, ToastElement } = useToast();

  const loadList = () => {
    setSelected(null);
    api.listUnmatchedPatients().then(r => { setItems(r.items); setTotal(r.total); }).catch(() => setItems([]));
  };
  useEffect(loadList, []);

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h2 className="admin-title">Разобрать неразобранное {total ? `(${total})` : ""}</h2>
      </div>

      {!selected ? (
        <UnmatchedList items={items} onSelect={setSelected} />
      ) : (
        <PatientReconcile patient={selected} onBack={loadList} showToast={showToast} />
      )}
      <ToastElement />
    </div>
  );
}

function UnmatchedList({ items, onSelect }) {
  if (items === null) return <div className="docs-empty">Загрузка...</div>;
  if (items.length === 0) return <div className="docs-empty">Разбирать нечего — все пациенты сопоставлены с YClients</div>;
  return (
    <table className="admin-table">
      <thead>
        <tr><th>ФИО</th><th>Телефон</th><th>Точка</th><th>Создан</th><th></th></tr>
      </thead>
      <tbody>
        {items.map(p => (
          <tr key={p.id} className={`cursor-pointer hover:bg-slate-50 ${p.yclientsSkippedAt ? "text-slate-400" : ""}`} onClick={() => onSelect(p)}>
            <td>
              {p.name}<PatientAlias name={p.name} alias={p.alias} />
              {p.yclientsSkippedAt && (
                <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 rounded-full px-1.5 py-0.5 ml-1.5">Пропущен</span>
              )}
            </td>
            <td>{p.phone || "—"}</td>
            <td>{p.location || "Общий"}</td>
            <td>{p.createdAt ? new Date(p.createdAt).toLocaleDateString("ru-RU") : "—"}</td>
            <td><button className="btn btn-ghost btn-sm">Сверить →</button></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PatientReconcile({ patient: initialPatient, onBack, showToast }) {
  const [patient, setPatient] = useState(initialPatient);
  const [matches, setMatches] = useState(null); // null = загрузка, [] = ничего не нашлось
  const [linking, setLinking] = useState(false);
  // Скан, для которого сейчас открыта модалка выбора записи (или null)
  const [activeDoc, setActiveDoc] = useState(null);
  const [docRecords, setDocRecords] = useState(null);
  // company_id той точки, откуда взят подтверждённый матч — нужен для запроса записей
  const [matchedCompanyId, setMatchedCompanyId] = useState(null);

  useEffect(() => {
    if (patient.yclientsClientId) return; // уже привязан — сразу к сканам, поиск клиента не нужен
    if (!patient.phone) { setMatches([]); return; }
    api.searchYclientsClientAdmin(patient.phone).then(r => setMatches(r.matches || [])).catch(() => setMatches([]));
  }, [patient.id]);

  // Второй пациент для склейки: из 409 «клиент уже привязан» или выбран вручную поиском.
  const [conflictPatient, setConflictPatient] = useState(null);
  const [mergeWithId, setMergeWithId] = useState(null);
  const [dupQuery, setDupQuery] = useState("");
  const debouncedDupQuery = useDebouncedValue(dupQuery, 250);
  const [dupResults, setDupResults] = useState([]);

  useEffect(() => {
    if (!debouncedDupQuery.trim()) { setDupResults([]); return; }
    let cancelled = false;
    api.listPatients({ q: debouncedDupQuery, limit: 8 })
      .then(r => { if (!cancelled) setDupResults(r.items.filter(p => p.id !== patient.id)); })
      .catch(() => { if (!cancelled) setDupResults([]); });
    return () => { cancelled = true; };
  }, [debouncedDupQuery, patient.id]);

  const handleLink = async (client) => {
    setLinking(true);
    setConflictPatient(null);
    try {
      const updated = await api.linkYclientsPatient(patient.id, client.id, client.companyId);
      setMatchedCompanyId(client.companyId);
      // Полная перезагрузка карточки — нужны sc сканы со свежим yclientsClientId для второго шага.
      const full = await api.getPatient(patient.id);
      setPatient({ ...full, yclientsClientId: updated.yclientsClientId });
      showToast("Привязано к «" + client.name + "»");
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.conflictPatient) setConflictPatient(err.response.data.conflictPatient);
      else showToast(err.response?.data?.error || "Не удалось привязать");
    } finally {
      setLinking(false);
    }
  };

  const [skipping, setSkipping] = useState(false);
  const handleSkip = async () => {
    setSkipping(true);
    try {
      await api.skipYclientsPatient(patient.id);
      showToast("Пропущено — опустили вниз списка");
      onBack();
    } catch {
      showToast("Не удалось пропустить");
      setSkipping(false);
    }
  };

  const unlinkedScans = (patient.scans || []).filter(s => !s.yclientsRecordId && !s.yclientsRecordSkippedAt);

  const openRecordPicker = async (doc) => {
    if (!matchedCompanyId) { showToast("Неизвестен филиал — переоткройте карточку"); return; }
    setActiveDoc(doc);
    setDocRecords(null);
    try {
      const { records } = await api.getYclientsClientRecordsAdmin(patient.yclientsClientId, matchedCompanyId);
      setDocRecords(records);
    } catch {
      setDocRecords([]);
    }
  };

  const handlePickRecord = async (recordId) => {
    const doc = activeDoc;
    setActiveDoc(null);
    try {
      await api.setDocumentYclientsRecord(doc.id, recordId);
      setPatient(p => ({ ...p, scans: p.scans.map(s => s.id === doc.id ? { ...s, yclientsRecordId: recordId || "skip" } : s) }));
      showToast("Запись привязана к скану");
    } catch {
      showToast("Не удалось привязать запись к скану");
    }
  };

  if (mergeWithId) {
    return (
      <PatientMergePanel
        firstId={patient.id}
        secondId={mergeWithId}
        showToast={showToast}
        onCancel={() => setMergeWithId(null)}
        onDone={onBack}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <button className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 w-fit" onClick={onBack}>
        <ArrowLeft className="h-3.5 w-3.5" /> К списку
      </button>

      <div className="rounded-2xl bg-white p-4 flex flex-col gap-3" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.05)" }}>
        <div>
          <p className="text-sm font-bold text-slate-900">{patient.name}<PatientAlias name={patient.name} alias={patient.alias} /></p>
          <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
            <Phone className="h-3 w-3" /> {patient.phone || "телефон не указан"}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              className="w-full rounded-lg border border-slate-200 pl-8 pr-3 py-1.5 text-xs"
              placeholder="Найти дубль этого пациента по ФИО или телефону, чтобы склеить"
              value={dupQuery}
              onChange={e => setDupQuery(e.target.value)}
            />
          </div>
          {dupResults.map(p => (
            <button key={p.id} className="text-left rounded-lg px-3 py-1.5 hover:bg-slate-50 flex items-center justify-between gap-2" onClick={() => setMergeWithId(p.id)}>
              <span className="text-xs text-slate-700 min-w-0 truncate">{p.name}<PatientAlias name={p.name} alias={p.alias} /> · {p.phone || "без телефона"}</span>
              <span className="text-[10px] font-semibold text-blue-600 flex-shrink-0 flex items-center gap-1"><Merge className="h-3 w-3" /> Склеить</span>
            </button>
          ))}
        </div>
      </div>

      {conflictPatient && (
        <div className="rounded-2xl bg-amber-50 p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <p className="text-xs text-amber-800">
            Этот клиент YClients уже привязан к пациенту «{conflictPatient.name}» ({conflictPatient.phone || "без телефона"}).
            Если это один человек — склейте карточки, отметив, какие обследования и покупки оставить.
          </p>
          <button className="btn btn-primary btn-sm flex items-center gap-1.5 flex-shrink-0" onClick={() => setMergeWithId(conflictPatient.id)}>
            <Merge className="h-3.5 w-3.5" /> Склеить пациентов
          </button>
        </div>
      )}

      {!patient.yclientsClientId ? (
        <div className="rounded-2xl bg-white p-4 flex flex-col gap-2" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.05)" }}>
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Совпадения в YClients</p>
          {matches === null ? (
            <p className="text-xs text-slate-400 text-center py-4">Поиск по всем точкам...</p>
          ) : matches.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-4">
              <p className="text-xs text-slate-400 text-center">
                {patient.phone ? "Совпадений не найдено" : "У пациента не указан телефон — искать нечем"}
              </p>
              <button className="btn btn-ghost btn-sm" disabled={skipping} onClick={handleSkip}>
                {skipping ? "..." : "Пропустить"}
              </button>
            </div>
          ) : (
            matches.map(c => (
              <button
                key={`${c.locationId}-${c.id}`}
                disabled={linking}
                onClick={() => handleLink(c)}
                className="text-left rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors px-4 py-3 flex items-center justify-between"
              >
                <div>
                  <p className="text-sm font-medium text-slate-800">{c.name}</p>
                  <p className="text-xs text-slate-400">{c.phone}</p>
                </div>
                <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 rounded-full px-2 py-0.5">{c.locationName}</span>
              </button>
            ))
          )}
        </div>
      ) : (
        <div className="rounded-2xl bg-white p-4 flex flex-col gap-2" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.05)" }}>
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
            Пациент привязан — сканы без записи ({unlinkedScans.length})
          </p>
          {unlinkedScans.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-4">Все сканы этого пациента уже привязаны к записям</p>
          ) : (
            unlinkedScans.map(s => (
              <div key={s.id} className="flex items-center justify-between px-3 py-2 border-b border-slate-50 last:border-0">
                <span className="text-xs text-slate-700">{s.name || "Скан"} · {s.createdAt ? new Date(s.createdAt).toLocaleDateString("ru-RU") : ""}{s.operatorName && <span className="text-slate-400"> · провёл {s.operatorName}</span>}</span>
                <button className="btn btn-ghost btn-sm" onClick={() => openRecordPicker(s)}>Привязать запись</button>
              </div>
            ))
          )}
        </div>
      )}

      {activeDoc && (
        <YClientsRecordPickerModal
          client={{ name: patient.name }}
          records={docRecords}
          onPick={handlePickRecord}
          onCancel={() => setActiveDoc(null)}
        />
      )}
    </div>
  );
}
